import { useSession } from 'src/providers/auth.provider'
import { BagLineIcon, CurecallButton, ECurecallButtonAppearance } from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import Link from 'src/components/navigation/link.component'
import { useState } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { PatientService } from 'src/services/patient.service'
import SectionTitle from 'src/components/section-title/section-title.component'
import { TFollowup } from 'src/types/followup'
import PatientFormComponent, {
  IAddNewPatientFormValues,
  parsePatientValues,
} from 'src/components/patient/patient-form/patient-form.component'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useSearchParams } from 'react-router-dom'
import { useTestAccountPool } from 'src/providers/test-account-pool.provider'
import FollowupCardList from 'src/components/followup-card-list/followup-card-list.component'
import { NAVIGATION_DEFAULT_LOCALE } from 'src/types/locales'
import { useParams } from 'react-router-dom'

export default function PatientAddPage() {
  const { currentUser } = useSession()
  const { projectId: projectIdParam } = useParams<{ projectId: string }>()
  const projectId = Number(projectIdParam)

  const { followups } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const methods = useForm<IAddNewPatientFormValues>({
    defaultValues: { locale: NAVIGATION_DEFAULT_LOCALE },
  })

  const {
    handleSubmit,
    formState: { errors },
  } = methods
  const { notify } = useNotification()
  const { t } = useTranslation()
  const { handleAddTestAccount } = useTestAccountPool()

  const oldParams = new URLSearchParams(searchParams.toString())

  // Pre-select the FollowupCard if there is only one available
  const [selectedFollowup, setSelectedFollowup] = useState<TFollowup | null>(followups[0] || null)
  const [hasCareGiver, setHasCareGiver] = useState(false)
  const [isTestAccount, setIsTestAccount] = useState(!!currentUser?.isAdminOrSales)
  const [isInDemoGroup, setIsInDemoGroup] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmitPatient = async (values: IAddNewPatientFormValues) => {
    setIsSubmitting(true)
    const patientData = parsePatientValues(values, isTestAccount, hasCareGiver)
    try {
      const patientResponse = await PatientService.create(projectId, patientData)
      await queryClient.invalidateQueries({ queryKey: ['patients', projectId] })

      // add patient to group demo :
      isInDemoGroup && handleAddTestAccount(patientResponse)

      selectedFollowup === null
        ? navigate(`/${projectId}/medical/patients/${patientResponse.id}&${oldParams}`)
        : navigate(
            `/${projectId}/medical/patients/${patientResponse.id}/add-patient-followup?followupId=${selectedFollowup.id}&${oldParams}`,
          )
      notify(
        {
          title: t('components.patient.add-or-edit.add-new-patient.patient-creation-success-title'),
          message: t('components.patient.add-or-edit.add-new-patient.patient-creation-success-message'),
          appearance: 'SUCCESS',
        },
        13000,
      )
    } catch (err: any) {
      notify(
        {
          title: t('common.actions.error'),
          message: err,
          appearance: 'ERROR',
        },
        13000,
      )
    }
    setIsSubmitting(false)
  }

  const onError = (formErrors: FieldErrors<IAddNewPatientFormValues>) => {
    // when there are errors in form scroll to top of the page
    if (Object.keys(formErrors).length) {
      window.scrollTo(0, 0)
      const formElement = document.getElementById('add-new-patient-form')
      if (formElement) formElement.scrollTop = 0
    }
  }

  return (
    <div className="c-font-nexa c-flex c-flex-col">
      <h1 className="c-text-xl c-font-black c-tracking-[0.48px] c-leading-[133%] c-pl-8 c-font-nexa c-border-b c-border-1 c-m-0">
        {t('components.patient.add-or-edit.add-new-patient.create-new-patient')}
      </h1>
      <FormProvider {...methods}>
        <form
          method="POST"
          action="#"
          onSubmit={handleSubmit(handleSubmitPatient, onError)}
          className="c-px-4 c-py-6 c-overflow-auto"
          id="add-new-patient-form"
          autoComplete="off"
        >
          <PatientFormComponent
            projectId={projectId}
            currentUser={currentUser}
            isTestAccount={isTestAccount}
            setIsTestAccount={setIsTestAccount}
            hasCaregiver={hasCareGiver}
            setHasCareGiver={setHasCareGiver}
            errors={errors}
            isInDemoGroup={isInDemoGroup}
            setIsInDemoGroup={setIsInDemoGroup}
          />
          <div className="c-flex c-flex-col c-gap-4 c-w-full">
            <SectionTitle
              icon={<BagLineIcon className="c-w-6" />}
              sectionTitle={t('components.patient.add-or-edit.add-new-patient.section-title')}
            />
            <FollowupCardList
              selectedFollowup={selectedFollowup}
              onClickOnCard={(followup: TFollowup) => setSelectedFollowup(followup)}
            />
          </div>
          <div className="c-flex c-justify-center c-items-center c-space-x-8  c-h-[184px]">
            <Link to={`/${projectId}/medical/patients`}>
              <CurecallButton appearance={ECurecallButtonAppearance.CANCEL}>
                {t('components.patient.add-or-edit.add-new-patient.cancel-button')}
              </CurecallButton>
            </Link>
            <CurecallButton
              type="submit"
              appearance={ECurecallButtonAppearance.PRIMARY}
              isLoading={isSubmitting}
              isDisabled={!selectedFollowup}
            >
              {t('components.patient.add-or-edit.add-new-patient.save-patient-button', {
                selectedFollowup: selectedFollowup === null ? 0 : selectedFollowup.id,
              })}
            </CurecallButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
